// Document requirements (TZ SME foundation, Increment F). Which documents each
// employment category must have on file — a per-tenant policy stored in
// employee-service and fed into the employee readiness score. The gateway
// exposes it under /api/v1/employees/*; gatewayApi attaches the access token
// and handles refresh.
import { gatewayApi } from '@/lib/hrApi';

/** Employment-type keys the policy matrix is keyed by. */
export const EMPLOYMENT_TYPE_KEYS = [
  'full_time',
  'part_time',
  'casual',
  'contract',
  'fixed_term',
  'specific_task',
  'intern',
  'seasonal',
  'consultant',
  'temporary',
  'other',
] as const;
export type EmploymentTypeKey = (typeof EMPLOYMENT_TYPE_KEYS)[number];

/** One selectable document type (e.g. { type: 'id', label: 'National ID' }). */
export interface DocumentTypeOption {
  type: string;
  label: string;
}

/** The rule set: required doc types per bucket. `default` applies to everyone;
 * `by_employment_type` adds per-category requirements; `foreign_extra` /
 * `driver_extra` are additive for foreign employees and drivers. */
export interface CustomDocType {
  key: string;
  label: string;
  /** Employment types it applies to; empty/absent = everyone. */
  applies_to?: string[];
  required?: boolean;
}

export interface DocumentRequirementRules {
  default: string[];
  by_employment_type: Record<string, string[]>;
  foreign_extra: string[];
  driver_extra: string[];
  /** Tenant-defined document types (compliance program) — no deployment needed. */
  custom_types?: CustomDocType[];
  /** Per-type metadata: expiration_required / hr_verification_required / reminder_days. */
  type_settings?: Record<string, { expiration_required?: boolean; hr_verification_required?: boolean; reminder_days?: number[] }>;
}

export interface DocumentRequirementsPolicy {
  /** The tenant's effective rules (may equal defaults if never customised). */
  rules: DocumentRequirementRules;
  /** The platform defaults, for "Reset to defaults". */
  defaults: DocumentRequirementRules;
  /** Valid document types (checkbox columns). */
  document_types: DocumentTypeOption[];
}

/** Per-employee required-vs-uploaded document status. */
export interface EmployeeDocumentStatus {
  required: { type: string; label: string }[];
  uploaded: string[];
  missing: { type: string; label: string }[];
  complete: boolean;
  requirements?: Array<{
    type: string; label: string; status: string;
    documentId?: string | null; expiresAt?: string | null; rejectionReason?: string | null;
    hrVerificationRequired?: boolean; expirationRequired?: boolean;
  }>;
}

const POLICY_PATH = '/api/v1/employees/document-requirements/policy';

/** GET the tenant policy (manager+). */
export const getDocumentRequirementsPolicy = async (): Promise<DocumentRequirementsPolicy> => {
  const { data } = await gatewayApi.get<DocumentRequirementsPolicy>(POLICY_PATH);
  return data;
};

/** PUT updated rules (tenant admin). 400 with a message on invalid types. */
export const putDocumentRequirementsPolicy = async (
  rules: DocumentRequirementRules,
): Promise<DocumentRequirementsPolicy> => {
  const { data } = await gatewayApi.put<DocumentRequirementsPolicy>(POLICY_PATH, { rules });
  return data;
};

/** GET one employee's required/uploaded/missing document status (self or manager+). */
export const getEmployeeDocumentStatus = async (
  employeeId: string,
): Promise<EmployeeDocumentStatus> => {
  const { data } = await gatewayApi.get<EmployeeDocumentStatus>(
    `/api/v1/employees/${employeeId}/document-status`,
  );
  return data;
};
